import { getAllDrafts, getSubmissions, saveDraft, saveSubmission } from '@/lib/db'
import {
  detectAudioContainerFromBytes,
  getAudioContainerFromMimeType,
  getMimeTypeForAudioContainer,
  normalizeRecordingBlob,
  type AudioContainer,
} from '@/lib/audio'
import type { DraftRecording, Submission } from '@/types'

const MIGRATION_FLAG = 'audio-migration-v3' // Incremented to v3 to correct mislabeled legacy WAV blobs

function hasRunMigration(): boolean {
  try {
    return localStorage.getItem(MIGRATION_FLAG) === '1'
  } catch {
    return false
  }
}

function markMigrationComplete(): void {
  try {
    localStorage.setItem(MIGRATION_FLAG, '1')
  } catch {
    // ignore
  }
}

/**
 * Validate blob integrity - checks if blob can be read and is valid
 */
async function validateBlob(blob: Blob | null | undefined): Promise<{
  isValid: boolean
  canRead: boolean
  size: number
  type: string
  detectedContainer: AudioContainer
  declaredContainer: AudioContainer
  hasContainerMismatch: boolean
  error?: string
}> {
  if (!blob) {
    return {
      isValid: false,
      canRead: false,
      size: 0,
      type: 'null',
      detectedContainer: 'unknown',
      declaredContainer: 'unknown',
      hasContainerMismatch: false,
      error: 'Blob is null',
    }
  }

  if (blob.size === 0) {
    return {
      isValid: false,
      canRead: false,
      size: 0,
      type: blob.type || 'unknown',
      detectedContainer: 'unknown',
      declaredContainer: getAudioContainerFromMimeType(blob.type),
      hasContainerMismatch: false,
      error: 'Blob has zero size',
    }
  }

  try {
    const arrayBuffer = await blob.arrayBuffer()
    if (arrayBuffer.byteLength === 0) {
      return {
        isValid: false,
        canRead: false,
        size: blob.size,
        type: blob.type || 'unknown',
        detectedContainer: 'unknown',
        declaredContainer: getAudioContainerFromMimeType(blob.type),
        hasContainerMismatch: false,
        error: 'Blob arrayBuffer is empty after read',
      }
    }

    const declaredContainer = getAudioContainerFromMimeType(blob.type)
    const detectedContainer = detectAudioContainerFromBytes(arrayBuffer)
    const hasContainerMismatch =
      declaredContainer !== 'unknown' &&
      detectedContainer !== 'unknown' &&
      declaredContainer !== detectedContainer

    return {
      isValid: true,
      canRead: true,
      size: blob.size,
      type: blob.type || 'unknown',
      detectedContainer,
      declaredContainer,
      hasContainerMismatch,
      error:
        declaredContainer === 'wav' && detectedContainer !== 'unknown' && detectedContainer !== 'wav'
          ? `Declared WAV blob appears to be ${detectedContainer}`
          : undefined,
    }
  } catch (error) {
    return {
      isValid: false,
      canRead: false,
      size: blob.size,
      type: blob.type || 'unknown',
      detectedContainer: 'unknown',
      declaredContainer: getAudioContainerFromMimeType(blob.type),
      hasContainerMismatch: false,
      error: error instanceof Error ? error.message : String(error),
    }
  }
}

async function convertToWavIfNeeded(blob: Blob | null | undefined): Promise<Blob | null> {
  if (!blob) return null

  // First validate the blob
  const validation = await validateBlob(blob)
  if (!validation.canRead || !validation.isValid) {
    console.warn('[MIGRATION] Blob is corrupted or unreadable, skipping conversion', {
      size: validation.size,
      type: validation.type,
      error: validation.error,
    })
    return null // Return null to skip this blob
  }

  if (validation.declaredContainer === 'wav' && !validation.hasContainerMismatch) {
    return blob
  }

  if (validation.detectedContainer === 'wav') {
    if (blob.type === 'audio/wav') {
      return blob
    }

    // Keep bytes unchanged and repair MIME metadata only.
    return new Blob([await blob.arrayBuffer()], { type: 'audio/wav' })
  }

  const correctedSourceType =
    getMimeTypeForAudioContainer(validation.detectedContainer) || blob.type || 'application/octet-stream'
  const conversionSource =
    validation.hasContainerMismatch && correctedSourceType !== blob.type
      ? new Blob([await blob.arrayBuffer()], { type: correctedSourceType })
      : blob

  // Convert WebM/unknown to WAV for Safari compatibility
  try {
    console.log('[MIGRATION] Converting blob to WAV', {
      originalType: blob.type,
      sourceType: conversionSource.type,
      detectedContainer: validation.detectedContainer,
      originalSize: blob.size,
    })
    const normalized = await normalizeRecordingBlob(conversionSource)
    const normalizedBuffer = await normalized.blob.arrayBuffer()
    const normalizedContainer = detectAudioContainerFromBytes(normalizedBuffer)
    const normalizedDeclaredContainer = getAudioContainerFromMimeType(normalized.blob.type)
    const isWavOutput = normalizedContainer === 'wav' || normalizedDeclaredContainer === 'wav'
    const wavBlob =
      isWavOutput
        ? new Blob([normalizedBuffer], { type: 'audio/wav' })
        : null

    if (!wavBlob) {
      console.warn('[MIGRATION] Conversion did not yield WAV bytes, preserving corrected source blob type', {
        originalType: blob.type,
        sourceType: conversionSource.type,
        normalizedType: normalized.blob.type,
        detectedContainer: validation.detectedContainer,
        normalizedContainer,
        normalizedDeclaredContainer,
      })
      return conversionSource
    }

    // Validate the converted blob
    const convertedValidation = await validateBlob(wavBlob)
    if (!convertedValidation.isValid) {
      console.warn('[MIGRATION] Converted blob is invalid', convertedValidation)
      return null
    }
    
    console.log('[MIGRATION] Conversion successful', {
      originalType: blob.type,
      sourceType: conversionSource.type,
      convertedType: wavBlob.type,
      convertedSize: wavBlob.size,
    })
    return wavBlob
  } catch (error) {
    console.error('[MIGRATION] Failed to convert blob to WAV', error)
    return null
  }
}

export async function migrateAudioBlobsForSafari(): Promise<void> {
  if (typeof window === 'undefined') return
  if (hasRunMigration()) {
    console.log('[MIGRATION] Migration already completed, skipping')
    return
  }

  console.log('[MIGRATION] Starting audio blob migration for Safari/iOS compatibility...')

  try {
    let draftsMigrated = 0
    let draftsSkipped = 0
    let draftsCorrupted = 0
    let submissionsMigrated = 0
    let submissionsSkipped = 0
    let submissionsCorrupted = 0

    // Drafts
    console.log('[MIGRATION] Processing drafts...')
    const drafts = await getAllDrafts()
    console.log(`[MIGRATION] Found ${drafts.length} drafts to process`)

    for (const draft of drafts) {
      try {
        const validation = await validateBlob(draft.blob)
        console.log(`[MIGRATION] Draft ${draft.id}:`, {
          size: validation.size,
          type: validation.type,
          canRead: validation.canRead,
          isValid: validation.isValid,
          declaredContainer: validation.declaredContainer,
          detectedContainer: validation.detectedContainer,
          hasContainerMismatch: validation.hasContainerMismatch,
          error: validation.error,
        })

        if (!validation.canRead || !validation.isValid) {
          console.warn(`[MIGRATION] Draft ${draft.id} is corrupted, skipping`, validation)
          draftsCorrupted++
          continue
        }

        const wavBlob = await convertToWavIfNeeded(draft.blob)
        if (!wavBlob) {
          console.warn(`[MIGRATION] Draft ${draft.id} conversion failed, skipping`)
          draftsSkipped++
          continue
        }

        if (wavBlob !== draft.blob) {
          const updated: DraftRecording = {
            ...draft,
            blob: wavBlob,
          }
          await saveDraft(updated)
          draftsMigrated++
          console.log(`[MIGRATION] Draft ${draft.id} migrated successfully`)
        } else {
          draftsSkipped++
        }
      } catch (error) {
        console.error(`[MIGRATION] Error processing draft ${draft.id}:`, error)
        draftsCorrupted++
      }
    }

    // Submissions with local blobs
    console.log('[MIGRATION] Processing submissions...')
    const submissions = await getSubmissions()
    const audioSubmissions = submissions.filter((s) => s.type === 'audio')
    console.log(`[MIGRATION] Found ${audioSubmissions.length} audio submissions to process`)

    for (const submission of audioSubmissions) {
      try {
        const currentBlob = submission.localBlob as Blob | undefined | null
        if (!currentBlob) {
          console.log(`[MIGRATION] Submission ${submission.id} has no localBlob, skipping`)
          submissionsSkipped++
          continue
        }

        const validation = await validateBlob(currentBlob)
        console.log(`[MIGRATION] Submission ${submission.id}:`, {
          size: validation.size,
          type: validation.type,
          canRead: validation.canRead,
          isValid: validation.isValid,
          declaredContainer: validation.declaredContainer,
          detectedContainer: validation.detectedContainer,
          hasContainerMismatch: validation.hasContainerMismatch,
          error: validation.error,
        })

        if (!validation.canRead || !validation.isValid) {
          console.warn(`[MIGRATION] Submission ${submission.id} blob is corrupted, skipping`, validation)
          submissionsCorrupted++
          continue
        }

        const wavBlob = await convertToWavIfNeeded(currentBlob)
        if (!wavBlob) {
          console.warn(`[MIGRATION] Submission ${submission.id} conversion failed, skipping`)
          submissionsSkipped++
          continue
        }

        if (wavBlob !== currentBlob) {
          const updated: Submission = {
            ...submission,
            localBlob: wavBlob,
          }
          await saveSubmission(updated)
          submissionsMigrated++
          console.log(`[MIGRATION] Submission ${submission.id} migrated successfully`)
        } else {
          submissionsSkipped++
        }
      } catch (error) {
        console.error(`[MIGRATION] Error processing submission ${submission.id}:`, error)
        submissionsCorrupted++
      }
    }

    const summary = {
      drafts: { total: drafts.length, migrated: draftsMigrated, skipped: draftsSkipped, corrupted: draftsCorrupted },
      submissions: {
        total: audioSubmissions.length,
        migrated: submissionsMigrated,
        skipped: submissionsSkipped,
        corrupted: submissionsCorrupted,
      },
    }

    console.log('[MIGRATION] Migration complete:', summary)
    markMigrationComplete()
  } catch (error) {
    console.error('[MIGRATION] Audio migration for Safari failed', error)
    // Don't mark as complete on error so it can retry
  }
}

export { MIGRATION_FLAG }
